import React, { useEffect, useRef, useState } from "react";
import exifr from "exifr";
import {
    Button,
    Grid,
    TextField,
    Typography,
    LinearProgress
} from "@material-ui/core";
import { compressImage, generateThumbnail, uploadToFirebase, onImageUploadComplete } from "./photoUtils";
import { db } from "../../../../firebase/firebasejs";

const localISOString = (date) => {
    const local = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
    return local.toISOString().replace("T", " ").slice(0, 23); // incluye .SSS
};

export default function PhotoBatchUploaderCamera({ eventId, userId }) {
    const [files, setFiles] = useState([]);
    const [photographerId, setPhotographerId] = useState(1);
    const [checkpoint, setCheckpoint] = useState("");
    const [timeOffset, setTimeOffset] = useState(0);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [progressMap, setProgressMap] = useState({});
    const [previewMap, setPreviewMap] = useState({});

    const [intervalSeconds, setIntervalSeconds] = useState(5);
    const [isCapturing, setIsCapturing] = useState(false);
    const [cameraReady, setCameraReady] = useState(false);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const captureIntervalRef = useRef(null);
    const wakeLockRef = useRef(null);

    const requestWakeLock = async () => {
        try {
            if ('wakeLock' in navigator) {
                wakeLockRef.current = await navigator.wakeLock.request("screen");
                console.log("Wake Lock activo");
                wakeLockRef.current.addEventListener("release", () => {
                    console.log("Wake Lock liberado");
                });
            }
        } catch (err) {
            console.error("No se pudo activar Wake Lock:", err);
        }
    };

    const releaseWakeLock = async () => {
        try {
            if (wakeLockRef.current) {
                await wakeLockRef.current.release();
                wakeLockRef.current = null;
            }
        } catch (err) {
            console.error("Error al liberar Wake Lock:", err);
        }
    };

    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: "environment",
                    width: { min: 1280, ideal: 1920, max: 3840 },
                    height: { min: 720, ideal: 1080, max: 2160 }
                }
            });
            videoRef.current.srcObject = stream;
            videoRef.current.onloadedmetadata = () => {
                videoRef.current.play();
                console.log("Resolución capturada:", videoRef.current.videoWidth, "x", videoRef.current.videoHeight);
            };
            setCameraReady(true);
        } catch (error) {
            console.error("Error al acceder a la cámara:", error);
        }
    };

    const stopCamera = () => {
        const stream = videoRef.current?.srcObject;
        if (stream) stream.getTracks().forEach((t) => t.stop());
        setCameraReady(false);
    };

    const capturePhoto = async () => {
        if (!videoRef.current || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const video = videoRef.current;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        return new Promise((resolve) => {
            canvas.toBlob(async (blob) => {
                const file = new File([blob], `photo_${Date.now()}.jpg`, {
                    type: "image/jpeg"
                });
                setFiles((prev) => {
                    const updated = [...prev, file];
                    return updated.slice(-10);
                });
                resolve();
            }, "image/jpeg", 0.9);
        });
    };

    useEffect(() => {
        return () => {
            stopCamera();
            clearInterval(captureIntervalRef.current);
            releaseWakeLock();
        };
    }, []);

    useEffect(() => {
        if (!eventId || !cameraReady) return;

        const ref = db.ref(`eventosCompletos/${eventId}/resultados`);

        const handleChange = (snapshot) => {
            const data = snapshot.val();
            if (!data || !data.to || data.to === "00:00:00") return;
            console.log(`Tiempo registrado en meta para ${snapshot.key}: ${data.to}`);
            capturePhoto();
        };

        ref.on("child_changed", handleChange);

        return () => {
            ref.off("child_changed", handleChange);
        };
    }, [eventId, cameraReady]);

    const handleStartCapture = async () => {
        if (!photographerId) {
            alert("Debes ingresar el ID del fotógrafo antes de iniciar.");
            return;
        }

        await requestWakeLock();

        setIsCapturing(true);

        captureIntervalRef.current = setInterval(() => {
            capturePhoto();
        }, intervalSeconds * 1000);
    };

    const handleStopCapture = () => {
        clearInterval(captureIntervalRef.current);
        setIsCapturing(false);
        stopCamera();
        releaseWakeLock();
    };

    useEffect(() => {
        if (!files.length) return;

        const processLastFile = async () => {
            const file = files[files.length - 1];
            setLoading(true);

            const exif = await exifr.parse(file, ["DateTimeOriginal"]);
            const fechaOriginal = exif?.DateTimeOriginal ? new Date(exif.DateTimeOriginal) : new Date();
            const fechaAjustada = new Date(fechaOriginal.getTime());
            fechaAjustada.setSeconds(fechaAjustada.getSeconds() + Number(timeOffset));

            const randomId = Math.floor(Math.random() * 100000);
            const fileParts = file.name.split(".");
            const extension = fileParts.pop();
            const baseName = fileParts.join(".");
            const filename = `${baseName}-${randomId}.${extension}`;

            const compressedBlob = await compressImage(file);
            const thumbnailBlob = await generateThumbnail(file);

            const updateProgress = (percent) => {
                setProgressMap((prev) => ({ ...prev, [filename]: percent }));
            };

            const { fullUrl, thumbUrl } = await uploadToFirebase(
                eventId,
                filename,
                compressedBlob,
                thumbnailBlob,
                updateProgress
            );

            const imagenObj = {
                fechaTomada: localISOString(fechaOriginal),
                horaAjustada: localISOString(fechaAjustada),
                filename: filename,
                fotografoID: photographerId,
                puntoFotografico: 1,
                url: fullUrl,
                urlThumb: thumbUrl,
                userUpload: userId,
                automatic: true
            };

            setResults((prev) => {
                const updated = [...prev, imagenObj];
                return updated.slice(-10);
            });
            setPreviewMap((prev) => ({ ...prev, [file.name]: URL.createObjectURL(file) }));
            onImageUploadComplete(imagenObj, eventId);
            setLoading(false);
        };

        processLastFile();
    }, [files]);

    return (
        <Grid container spacing={2} direction="column">
            <Typography variant="h5">Captura de Fotos desde Cámara</Typography>

            <div style={{ position: "relative", width: "100%" }}>
                <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    style={{ width: "100%", maxHeight: 800, cursor: "pointer" }}
                    onDoubleClick={() => videoRef.current?.requestFullscreen?.()}
                />
                <Button
                    onClick={capturePhoto}
                    variant="contained"
                    color="secondary"
                    style={{
                        position: "absolute",
                        bottom: 20,
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 10
                    }}
                >
                    Tomar Foto
                </Button>
            </div>

            <canvas ref={canvasRef} style={{ display: "none" }} />

            <TextField
                label="ID del Fotógrafo"
                value={photographerId}
                onChange={(e) => setPhotographerId(e.target.value)}
                fullWidth
            />
            <TextField
                label="Ajuste de Hora (segundos)"
                type="number"
                value={timeOffset}
                onChange={(e) => setTimeOffset(e.target.value)}
                fullWidth
            />
            <TextField
                label="Intervalo entre fotos (segundos)"
                type="number"
                value={intervalSeconds}
                onChange={(e) => setIntervalSeconds(Number(e.target.value))}
                fullWidth
            />

            <Grid item>
                <Button
                    onClick={startCamera}
                    color="primary"
                    variant="contained"
                    disabled={cameraReady}
                >
                    Iniciar Cámara
                </Button>
                <Button
                    onClick={handleStartCapture}
                    color="secondary"
                    variant="contained"
                    disabled={!cameraReady || isCapturing}
                    style={{ marginLeft: 10 }}
                >
                    Comenzar Captura
                </Button>
                <Button
                    onClick={handleStopCapture}
                    color="default"
                    variant="contained"
                    disabled={!isCapturing}
                    style={{ marginLeft: 10 }}
                >
                    Detener Captura
                </Button>
            </Grid>

            <Grid item>
                {files.map((file, idx) => {
                    const result = results.find(r => r.filename.includes(file.name.split(".")[0])) || null;
                    const filenameKey = result?.filename || Object.keys(progressMap).find(k => k.startsWith(file.name.split(".")[0])) || file.name;
                    const progress = progressMap[filenameKey] || 0;

                    return (
                        <Grid container item spacing={2} alignItems="center" key={idx} style={{ marginBottom: 10 }}>
                            <Grid item>
                                <img
                                    src={result?.urlThumb || previewMap[file.name]}
                                    alt="thumb"
                                    style={{ maxWidth: 100, height: "auto", cursor: "pointer" }}
                                    onClick={() => window.open(result?.url || previewMap[file.name], "_blank")}
                                />
                            </Grid>
                            <Grid item xs>
                                <Typography variant="body2">{result?.filename || file.name}</Typography>
                                {result ? (
                                    <>
                                        <Typography variant="caption">{result.fechaTomada}</Typography><br />
                                        <Typography variant="caption">{result.horaAjustada}</Typography>
                                        <Typography variant="caption" color="primary" display="block">Carga completada</Typography>
                                    </>
                                ) : (
                                    <LinearProgress variant="determinate" value={progress} />
                                )}
                            </Grid>
                        </Grid>
                    );
                })}
            </Grid>
        </Grid>
    );
}
